import React, { useState, useEffect } from 'react';
import { collection, addDoc, doc, onSnapshot } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase/config';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Play, Square, Video, Plus, Download } from 'lucide-react';

interface AttendanceRecord {
  name: string;
  durationMinutes: number;
  joined: string;
  left: string;
}

interface ClassData {
  id?: string;
  className: string;
  meetingLink?: string;
  spaceName?: string;
  conferenceRecord?: string;
  status: 'pending' | 'active' | 'completed';
  attendance?: AttendanceRecord[];
  recordingLink?: string | null;
}

export const TeacherClass = () => {
  const [className, setClassName] = useState('');
  const [activeClass, setActiveClass] = useState<ClassData | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const functions = getFunctions();
  const createClassFn = httpsCallable<{ className: string }, { meetingLink: string, spaceName: string }>(functions, 'createClass');
  const startClassAndCaptureFn = httpsCallable<{ classId: string, spaceName: string }, { conferenceRecord: string | null }>(functions, 'startClassAndCapture');
  const saveClassDataFn = httpsCallable<{ classId: string, conferenceRecord: string }, { attendance: AttendanceRecord[], recordingLink: string | null }>(functions, 'saveClassData');

  // Listen to active class changes
  useEffect(() => {
    const auth = getAuth();
    const uid = auth.currentUser?.uid;
    if (!activeClass?.id || !uid) return;
    
    // Use the workspace pattern as per context
    const classDocPath = `teaching_workspaces/${uid}/classes/${activeClass.id}`;
    
    const unsub = onSnapshot(doc(db, classDocPath), (docSnapshot) => {
      if (docSnapshot.exists()) {
        setActiveClass({ id: docSnapshot.id, ...docSnapshot.data() } as ClassData);
      }
    });
    return () => unsub();
  }, [activeClass?.id]);

  const handleCreateClass = async () => {
    const auth = getAuth();
    const uid = auth.currentUser?.uid;
    if (!className || !uid) return;
    
    setIsLoading(true);
    try {
      // Create initial doc in Firestore using the workspace pattern
      const classesRef = collection(db, `teaching_workspaces/${uid}/classes`);
      const classRef = await addDoc(classesRef, {
        className,
        status: 'pending',
        createdAt: new Date().toISOString()
      });

      // Call Cloud Function to create Meet Space
      const result = await createClassFn({ className });
      const { meetingLink, spaceName } = result.data;

      setActiveClass({ id: classRef.id, className, meetingLink, spaceName, status: 'pending' });
    } catch (error) {
      console.error("Failed to create class:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartClass = async () => {
    if (!activeClass?.id || !activeClass?.spaceName) return;
    setIsLoading(true);
    try {
      const result = await startClassAndCaptureFn({ 
        classId: activeClass.id, 
        spaceName: activeClass.spaceName 
      });
      // The snapshot listener will update the local state with conferenceRecord & status
      if (!result.data.conferenceRecord) {
        alert("Meeting hasn't started or no conference record found yet. Please make sure you joined the call.");
      }
    } catch (error) {
      console.error("Failed to start class:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEndClass = async () => {
    if (!activeClass?.id || !activeClass?.conferenceRecord) return;
    setIsLoading(true);
    try {
      await saveClassDataFn({ 
        classId: activeClass.id, 
        conferenceRecord: activeClass.conferenceRecord 
      });
      // Snapshot listener updates local state to completed
    } catch (error) {
      console.error("Failed to end class and save data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="max-w-3xl mx-auto shadow-sm border border-gray-100 dark:border-white/5 rounded-none">
      <CardHeader className="border-b border-gray-100 dark:border-white/5 bg-gray-50/50 dark:bg-white/5">
        <CardTitle className="text-xl font-bold tracking-tight flex items-center gap-2">
          <Video className="w-5 h-5 text-blue-500" />
          Google Meet Integration (MVP)
        </CardTitle>
        <CardDescription>
          Create, start, and automatically capture attendance & recordings for your remote classes.
        </CardDescription>
      </CardHeader>
      
      <CardContent className="p-6 space-y-6">
        {!activeClass ? (
          <div className="flex items-center gap-4">
            <Input 
              value={className} 
              onChange={e => setClassName(e.target.value)} 
              placeholder="e.g. Advanced Calculus (Week 2)" 
              className="rounded-none border-gray-200"
            />
            <Button 
              onClick={handleCreateClass} 
              disabled={isLoading || !className} 
              className="rounded-none bg-blue-600 hover:bg-blue-700 font-bold whitespace-nowrap"
            >
              <Plus className="w-4 h-4 mr-2" />
              {isLoading ? "Creating..." : "Create Class"}
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="p-4 bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/30">
              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">{activeClass.className}</h3>
              {activeClass.meetingLink && (
                <div className="flex flex-col gap-1 mb-4">
                  <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Meeting Link</span>
                  <a href={activeClass.meetingLink} target="_blank" rel="noopener noreferrer" className="text-blue-600 font-medium hover:underline text-sm break-all">
                    {activeClass.meetingLink}
                  </a>
                </div>
              )}
              
              <div className="flex flex-wrap items-center gap-3">
                {activeClass.status === 'pending' && (
                  <Button onClick={handleStartClass} disabled={isLoading} className="rounded-none bg-green-600 hover:bg-green-700 border-none flex-1 md:flex-none">
                    <Play className="w-4 h-4 mr-2" /> Start Class
                  </Button>
                )}
                
                {activeClass.status === 'active' && (
                  <Button onClick={handleEndClass} disabled={isLoading} variant="destructive" className="rounded-none flex-1 md:flex-none font-bold">
                    <Square className="w-4 h-4 mr-2" /> End Class & Save Attendance + Recording
                  </Button>
                )}
              </div>
            </div>

            {activeClass.status === 'completed' && activeClass.attendance && (
              <div className="space-y-4">
                <div className="flex items-center justify-between border-b pb-2">
                  <h4 className="text-sm font-bold uppercase tracking-wider text-gray-500">Class Summary</h4>
                  {activeClass.recordingLink ? (
                    <a href={activeClass.recordingLink} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" size="sm" className="rounded-none border-blue-200 text-blue-600 hover:bg-blue-50">
                        <Download className="w-4 h-4 mr-2" /> Download Recording
                      </Button>
                    </a>
                  ) : (
                    <span className="text-xs text-gray-400 italic">No recording available</span>
                  )}
                </div>

                <div className="border border-gray-100 dark:border-white/5 rounded-none overflow-hidden">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-gray-50 dark:bg-white/5 text-xs uppercase text-gray-500 border-b border-gray-100 dark:border-white/5">
                      <tr>
                        <th className="px-4 py-3 font-bold">Participant Name</th>
                        <th className="px-4 py-3 font-bold text-right">Minutes Attended</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-white/5 bg-white dark:bg-transparent">
                      {activeClass.attendance.map((record, i) => (
                        <tr key={i} className="hover:bg-gray-50 dark:hover:bg-white/5">
                          <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">{record.name}</td>
                          <td className="px-4 py-3 text-right text-gray-500 tabular-nums">
                            {record.durationMinutes} min
                          </td>
                        </tr>
                      ))}
                      {activeClass.attendance.length === 0 && (
                        <tr>
                          <td colSpan={2} className="px-4 py-6 text-center text-gray-400 italic">No participants recorded.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TeacherClass;
